import React, { memo, useMemo } from "react";
import { Loader2, Sparkles, Leaf, Award, Star } from "lucide-react";

// Ensure newline here separates external from internal
import ListingDisplayCard from "../P2P/ListingDisplayCard";

// Resolve image URLs - prepend control plane for relative /assets/ paths
const CONTROL_PLANE_URL = "https://agentic-commerce-control-plane.pages.dev";
const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return "/images/placeholder.svg";
  if (imageUrl.startsWith("/assets/")) {
    return `${CONTROL_PLANE_URL}${imageUrl}`;
  }
  return imageUrl;
};

// Quality tier badge configuration
const QUALITY_BADGES = {
  luxury: { label: "Luxury", className: "bg-amber-100 text-amber-800", icon: Award },
  premium: { label: "Premium", className: "bg-purple-100 text-purple-800", icon: Star },
  "mid-range": { label: "Quality", className: "bg-blue-100 text-blue-800", icon: null },
  budget: { label: "Value", className: "bg-green-100 text-green-800", icon: Leaf },
};

// The product card component, extracted and memoized to prevent unnecessary re-renders
export const ProductCard = memo(function ProductCard({
  product,
  productCardClasses,
  setSelectedProduct,
}) {
  // Get quality tier badge if available
  const qualityTier = product.quality_tier?.toLowerCase();
  const badge = qualityTier ? QUALITY_BADGES[qualityTier] : null;
  const BadgeIcon = badge?.icon;

  // Get first key feature if available
  const keyFeature = product.key_features?.[0];

  return (
    // eslint-disable-next-line
    <div
      onClick={() => setSelectedProduct(product)}
      className={productCardClasses}
    >
      {/* Square image container with fixed aspect ratio */}
      <div className="relative w-full pt-[100%] overflow-hidden mb-1">
        <img
          src={resolveImageUrl(Array.isArray(product.image) ? product.image[0] : product.image)}
          alt={product.title || "Product image"}
          className="absolute inset-0 w-full my-auto object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.src = "/images/placeholder.svg";
          }}
        />
        {/* Quality tier badge overlay */}
        {badge && (
          <div className={`absolute top-1 left-1 px-1.5 py-0.5 text-xs font-medium rounded flex items-center gap-1 ${badge.className}`}>
            {BadgeIcon && <BadgeIcon size={10} />}
            {badge.label}
          </div>
        )}
        {/* Enriched indicator */}
        {product.is_enriched && (
          <div className="absolute top-1 right-1 p-1 bg-white/90 rounded-full" title="AI Enhanced">
            <Sparkles size={12} className="text-penta-mint" />
          </div>
        )}
      </div>

      {/* Brand */}
      {product.brand && (
        <div className="text-xs text-penta-grey-medium truncate mt-1 uppercase tracking-wide">
          {product.brand}
        </div>
      )}

      {/* Truncated title on a single line */}
      <div className="text-sm truncate text-penta-black">
        {product.title}
      </div>

      {/* Key feature (if available) */}
      {keyFeature && (
        <div className="text-xs text-penta-grey-medium truncate">
          {keyFeature}
        </div>
      )}

      {/* Price */}
      <div className="text-penta-grey-medium text-sm mt-1">
        {product.maxPrice && product.minPrice !== product.maxPrice
          ? `$${product.minPrice} - $${product.maxPrice}`
          : `$${product.minPrice || product.maxPrice}`}
      </div>
    </div>
  );
});

const ResultGrid = memo(function ResultGrid({
  setSelectedProduct,
  results,
  isLoading,
  isMobileInline,
  containerClassName,
}) {
  // Choose between horizontal scroll layout (mobile) or grid layout (desktop)
  const resultsContainerClasses = isMobileInline
    ? "flex overflow-x-auto hide-scrollbar py-1" // Horizontal scrolling container with snap
    : containerClassName ||
      "grid grid-cols-1 @md/ResultsGrid:grid-cols-2 @lg/ResultsGrid:grid-cols-3 @xl/ResultsGrid:grid-cols-3 @2xl/ResultsGrid:grid-cols-4 gap-4"; // Original grid layout

  // Product card classes differ between layouts - responsive widths for mobile
  const productCardClasses = isMobileInline
    ? "flex-shrink-0 w-[130px] sm:w-[150px] mr-3 flex p-2 flex-col cursor-pointer overflow-hidden bg-white border border-neutral-200"
    : "flex flex-col cursor-pointer p-3 overflow-hidden bg-white border border-neutral-200 hover:border-penta-mint transition-colors duration-200";

  // Card classes specific to P2P card in this grid context - responsive widths
  const p2pCardClasses = isMobileInline
    ? "flex-shrink-0 w-[150px] sm:w-[170px] mr-3 cursor-pointer"
    : "cursor-pointer";

  // Ensure results is always an array
  const resultsArray = useMemo(() => {
    return Array.isArray(results) ? results : results ? [results] : [];
  }, [results]);

  // Memoize the results to prevent unnecessary re-renders - MUST BE BEFORE ANY CONDITIONALS
  const memoizedResults = useMemo(() => {
    if (!resultsArray.length) return [];

    return resultsArray
      .filter((product) => {
        if (!product?.image) return false;
        const imageUrl = Array.isArray(product.image)
          ? product.image[0]
          : product.image;
        // Validate image URL - must be a valid-looking URL
        if (typeof imageUrl !== "string" || imageUrl.length === 0) return false;
        // Filter out known broken/malformed URLs
        if (imageUrl.includes("demandware.static") || imageUrl.includes("demandware.stats")) return false;
        // Must start with http/https or be a relative path
        return imageUrl.startsWith("http") || imageUrl.startsWith("/");
      })
      .map((product) => ({
        ...product,
        // Ensure ID exists for key, fallback needed
        key: product.id || product.link || `${product.title}-${Math.random()}`,
      }));
  }, [resultsArray]);

  // Return early with loading state if no results
  if (isLoading && !memoizedResults.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 h-full">
        <Loader2 className="h-6 w-6 animate-spin text-penta-mint" />
        <p className="mt-2 text-penta-grey-medium">Searching for products...</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className={resultsContainerClasses}>
        {memoizedResults.map((product, i) => {
          // Check if it's a P2P item
          if (product.item_type === "p2p") {
            return (
              <div
                key={product.key}
                className={`${p2pCardClasses} ${i === 0 && isMobileInline ? "ml-3" : ""}`}
              >
                <ListingDisplayCard
                  listing={product} // Pass the whole product/metadata object
                  // Note: setSelectedProduct might need different handling for P2P items
                  // onClick={() => console.log('P2P Card Clicked', product.id)}
                />
              </div>
            );
          } else {
            // Render the original ProductCard for non-P2P items
            return (
              <ProductCard
                setSelectedProduct={setSelectedProduct}
                key={product.key}
                product={product}
                productCardClasses={`${productCardClasses} ${i === 0 && isMobileInline ? "ml-3" : ""}`}
              />
            );
          }
        })}
        {/* Loading spinner at bottom when loading more (desktop only) */}
        {isLoading && resultsArray.length > 0 && !isMobileInline && (
          <div className="flex flex-col justify-center items-center p-4 mt-4 col-span-full">
            <div className="flex items-center mb-2">
              <Loader2 className="h-6 w-6 animate-spin text-penta-mint" />
              <span className="ml-2 text-penta-grey-medium">
                Loading more results...
              </span>
            </div>
          </div>
        )}

        {/* Loading spinner for mobile */}
        {isLoading && resultsArray.length > 0 && isMobileInline && (
          <div className="flex-shrink-0 flex items-center p-3 ml-3">
            <Loader2 className="h-5 w-5 animate-spin text-penta-mint" />
          </div>
        )}
      </div>
    </div>
  );
});

// Export the memoized component
export default ResultGrid;
